// components/expenses/ManageCategoriesDialog.jsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Paper,
    Stack,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { expenseCategoryAPI } from '../../services/api';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const ManageCategoriesDialog = ({ open, onClose, categories, onRefresh }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ name: '', description: '' });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (category) => {
        setEditingId(category._id);
        setForm({
            name: category.name || '',
            description: category.description || ''
        });
    };

    const handleClearForm = () => {
        setEditingId(null);
        setForm({ name: '', description: '' });
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            toast.error(t('common.nameRequired', 'Name is required'));
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description?.trim() || undefined
            };

            if (editingId) {
                await expenseCategoryAPI.update(editingId, payload);
                toast.success(t('expenses.categoryUpdateSuccess', 'Category updated successfully'));
            } else {
                await expenseCategoryAPI.create(payload);
                toast.success(t('expenses.categoryCreateSuccess', 'Category created successfully'));
            }
            handleClearForm();
            onRefresh();
        } catch (err) {
            console.error('Category save error:', err);
            toast.error(err.response?.data?.message || t('common.saveError', 'Failed to save category'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setLoading(true);
        try {
            await expenseCategoryAPI.delete(deleteId);
            toast.success(t('expenses.categoryDeleteSuccess', 'Category deleted successfully'));
            onRefresh();
        } catch (err) {
            console.error('Category delete error:', err);
            toast.error(err.response?.data?.message || t('common.deleteError', 'Failed to delete category'));
        } finally {
            setLoading(false);
            setConfirmOpen(false);
            setDeleteId(null);
        }
    };

    const handleClose = () => {
        handleClearForm();
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>{t('expenses.manageCategories', 'Manage Expense Categories')}</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
                            gap: 3,
                            mt: 1
                        }}
                    >
                        {/* Form Section */}
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {editingId ? t('common.editCategory', 'Edit Category') : t('common.addCategory', 'Add Category')}
                            </Typography>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    name="name"
                                    label={t('common.name', 'Name')}
                                    value={form.name}
                                    onChange={handleChange}
                                    required
                                    size="small"
                                />
                                <TextField
                                    fullWidth
                                    name="description"
                                    label={t('common.description', 'Description')}
                                    value={form.description}
                                    onChange={handleChange}
                                    multiline
                                    rows={2}
                                    size="small"
                                />
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={loading || !form.name.trim()}
                                        startIcon={loading ? <CircularProgress size={16} /> : editingId ? null : <Add />}
                                    >
                                        {editingId ? t('common.saveChanges', 'Save Changes') : t('common.create', 'Create')}
                                    </Button>
                                    {editingId && (
                                        <Button onClick={handleClearForm}>
                                            {t('common.cancel', 'Cancel')}
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </Paper>

                        {/* Categories List Section */}
                        <Paper variant="outlined">
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <Typography variant="h6">
                                    {t('expenses.categoriesList', 'Categories')} ({categories.length})
                                </Typography>
                            </Box>
                            <List sx={{ maxHeight: 350, overflow: 'auto' }}>
                                {categories.length === 0 ? (
                                    <ListItem>
                                        <ListItemText
                                            primary={t('expenses.noCategories', 'No categories found')}
                                            secondary={t('expenses.createFirstCategory', 'Create your first category using the form')}
                                        />
                                    </ListItem>
                                ) : (
                                    categories.map((category, index) => (
                                        <React.Fragment key={category._id}>
                                            <ListItem
                                                secondaryAction={
                                                    <Stack direction="row" spacing={0.5}>
                                                        <Tooltip title={t('common.edit', 'Edit')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEdit(category)}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('common.delete', 'Delete')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setDeleteId(category._id);
                                                                    setConfirmOpen(true);
                                                                }}
                                                            >
                                                                <Delete fontSize="small" color="error" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                }
                                            >
                                                <ListItemText
                                                    primary={category.name}
                                                    secondary={category.description || t('common.noDescription', 'No description')}
                                                />
                                            </ListItem>
                                            {index < categories.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleClose}>
                        {t('common.close', 'Close')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmOpen}
                title={t('expenses.deleteCategoryTitle', 'Delete Category?')}
                message={t('expenses.deleteCategoryMessage', 'Are you sure you want to delete this category? This action cannot be undone.')}
                onConfirm={handleDelete}
                onClose={() => {
                    setConfirmOpen(false);
                    setDeleteId(null);
                }}
            />
        </>
    );
};

export default ManageCategoriesDialog;